/**
 * The one client-side path to a `documents` row's file (SD-7.6 AC-2, FR-23).
 *
 * WHY THIS EXISTS
 * ---------------
 * Until SD-7.6 every row in `documents` lived in the `project-documents` bucket, so every read
 * path in the app hardcodes that bucket name — `DocumentsTabV2.tsx`, `NewProjectFormTab.tsx`,
 * `ProjectOverviewHeader.tsx`, `DesignDocsTab.tsx` (one of them with a literal
 * `// Always use project-documents bucket` comment). It was true, so nobody had to think about it.
 *
 * FR-23 files the generated solar proposal PDF in `documents`, and that file lives in the
 * `proposals` bucket. Read through any of those hardcoded paths it is a 404 — and a *silent* one,
 * because the row itself is perfectly well-formed. The delete is worse: it reports success while
 * removing nothing.
 *
 * `documents.storage_bucket` (migration 20260805n) is the fix, and this module is the only thing
 * that should read it.
 *
 * NULL MEANS `project-documents`
 * ------------------------------
 * That is what makes the column additive: every historical row is correct without a backfill, and
 * every writer that predates it keeps working. `bucketForDocument` encodes that default in one
 * place so no call site has to remember it.
 *
 * THERE IS DELIBERATELY NO CROSS-BUCKET FALLBACK
 * ----------------------------------------------
 * If signing in the recorded bucket fails, this returns null. It does NOT then try
 * `project-documents`. CB-11.1 made the same choice for contracts and its reasoning holds exactly:
 * "A fallback would work, and because it worked nobody would notice it had regressed." A resolver
 * that quietly searches buckets would hide a wrong `storage_bucket` value forever, and the whole
 * point of the column is that the bucket is recorded rather than guessed.
 */

import { supabase } from '@/lib/supabase';

/** Where `documents` rows lived before SD-7.6, and where they still live unless told otherwise. */
export const DEFAULT_DOCUMENTS_BUCKET = 'project-documents';

/**
 * One hour. Long enough to read or print a proposal; short enough that a forwarded link stops
 * working. Matches `CONTRACT_URL_TTL_SECONDS` and the existing `createSignedUrl(path, 3600)` calls
 * this replaces, so no download gets shorter-lived than it was.
 */
export const DOCUMENT_URL_TTL_SECONDS = 3600;

/** The minimum shape this module needs. Deliberately not the full row type: any caller holding a
 *  `storage_path` and a `storage_bucket` can use it, including partial selects. */
export interface StoredDocumentRef {
  storage_path?: string | null;
  storage_bucket?: string | null;
}

/**
 * The bucket a document actually lives in.
 *
 * `null`, `undefined` and `''` all mean the historical default — an empty string because a form or
 * a JSON round-trip can turn a NULL into one, and treating that as a bucket named `''` would fail
 * in a way nobody would read as "missing".
 */
export function bucketForDocument(doc: StoredDocumentRef | null | undefined): string {
  const bucket = doc?.storage_bucket;
  if (typeof bucket === 'string' && bucket.trim()) return bucket.trim();
  return DEFAULT_DOCUMENTS_BUCKET;
}

/**
 * A signed URL for a document row's file, or null.
 *
 * Null is a real answer and callers must surface it rather than substituting the raw path or
 * another bucket — see the module docstring. A broken download that reports itself is fixable; one
 * that silently resolves from the wrong bucket is not.
 */
export async function documentStorageUrl(
  doc: StoredDocumentRef | null | undefined,
  expiresInSeconds: number = DOCUMENT_URL_TTL_SECONDS,
): Promise<string | null> {
  const path = doc?.storage_path?.trim();
  if (!path) return null;

  const bucket = bucketForDocument(doc);
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresInSeconds);
    if (error || !data?.signedUrl) {
      console.error('[documentStorageUrl] failed to sign', bucket, path, error);
      return null;
    }
    return data.signedUrl;
  } catch (err) {
    console.error('[documentStorageUrl] threw while signing', bucket, path, err);
    return null;
  }
}

/**
 * Remove a document's file from the bucket it actually lives in.
 *
 * The half that is easy to forget: a delete aimed at the wrong bucket returns SUCCESS from Supabase
 * storage — removing a non-existent object is not an error — so the row disappears from the UI
 * while the file stays behind. For a customer-facing proposal PDF in a bucket that is still public
 * (SD-7.7 has not landed), that is a document that remains fetchable after being "deleted".
 *
 * Returns whether the remove call itself succeeded.
 */
export async function removeDocumentFile(doc: StoredDocumentRef | null | undefined): Promise<boolean> {
  const path = doc?.storage_path?.trim();
  if (!path) return false;

  const bucket = bucketForDocument(doc);
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);
    if (error) {
      console.error('[removeDocumentFile] failed to remove', bucket, path, error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[removeDocumentFile] threw while removing', bucket, path, err);
    return false;
  }
}
